import pull from 'lodash/pull';

type ReceiveStateCallback<S> = (state: S | null) => void;
type UnregisterHook = () => void;

/**
 * Ensure that exactly one instance (if one exists) of a component receives a given
 * state object at any one time.
 *
 * (This is a helper class for the `Dialogs` component, which takes its state from a global
 * function, but I'm paranoid about it being re-mounted multiple times or mounted twice at
 * once.)
 *
 * If multiple instances of a component are mounted, only one will receive the "current state".
 * When it's dismounted, one of the other instances will be chosen to receive that state.
 * If an instance is temporarily unmounted and re-mounted, this makes sure that component
 * will receive the same state it previously had.
 *
 * This doesn't do anything clever like call "setState" on components or track when they
 * are unmounted.
 *
 * Instead, this class exposes a small callback-based API. Components are expected to
 * register and unregister themselves. Components are also expected to pass a callback
 * that assigns the state (or lack of state) to their own internal state via setState().
 */
export class SingletonComponentStateManager<S> {
    private initialState: S;
    private instanceName: string;
    private instanceCallbacks: ReceiveStateCallback<S>[] = [];
    private _currentState: S;
    private _primaryInstanceCallback: ReceiveStateCallback<S> | null = null;

    /**
     * @param instanceName  Name of the instance to show in console warning messages
     * @param initialState  State that the primary instance should have at first
     */
    constructor(instanceName: string, initialState: S) {
        this.instanceName = instanceName;
        this.instanceState = this.initialState = initialState;
    }

    /**
     * Update the current
     */
    public get instanceState(): S {
        return this._currentState;
    }

    public set instanceState(state: S) {
        this._currentState = state;

        // Pass state to instance
        if (this.primaryInstanceCallback) {
            this.primaryInstanceCallback(state);
        }
    }

    public registerInstance(instance: ReceiveStateCallback<S>): UnregisterHook {

        if (this.instanceCallbacks.length > 0) {
            console.warn(`${this.instanceName} should not appear more than once in the React hierarchy`);
        }

        this.instanceCallbacks.push(instance);

        if (this.primaryInstanceCallback === null) {
            this.primaryInstanceCallback = instance;
        }

        // Unregister hook
        return () => {

            // Remove all appearances of `this` (of which there should be exactly 1) from `INSTANCES`
            pull(this.instanceCallbacks, instance);

            if (this.primaryInstanceCallback === instance) {
                this.primaryInstanceCallback = (this.instanceCallbacks.length > 0) ? this.instanceCallbacks[0] : null;
            }
        };
    }

    public reset() {
        this.instanceCallbacks = [];
        this._currentState = this.initialState;
        this._primaryInstanceCallback = null;
    }

    private set primaryInstanceCallback(instance: ReceiveStateCallback<S> | null) {
        if (instance === this._primaryInstanceCallback) {
            return;
        }

        // Unregister existing
        if (this._primaryInstanceCallback) {
            this._primaryInstanceCallback(null);
        }

        this._primaryInstanceCallback = instance;

        // Register new
        if (this._primaryInstanceCallback) {
            this._primaryInstanceCallback(this.instanceState);
        }
    }

    private get primaryInstanceCallback() {
        return this._primaryInstanceCallback;
    }
}
